import { useCallback, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useMediaGalleryContext, useTheme } from '@volkswagen-onehub/components-core';

type DragStart = {
  x: number;
  y: number;
  lastIndex: number;
  startedAt: number;
  hasDragged: boolean;
};

type UseMediaGalleryDesktopDragProps = {
  disabled?: boolean;
};

const DRAG_TOLERANCE = 10;

export const useMediaGalleryDesktopDrag = ({
  disabled = false,
}: UseMediaGalleryDesktopDragProps = {}) => {
  const { direction } = useTheme();
  const { dispatch, getState } = useMediaGalleryContext();

  const dragStartRef = useRef<DragStart | null>(null);
  const preventClickRef = useRef(false);

  const endDrag = useCallback(
    (container: HTMLDivElement, pointerId: number) => {
      if (container.hasPointerCapture(pointerId)) {
        container.releasePointerCapture(pointerId);
      }

      const dragStart = dragStartRef.current;
      dragStartRef.current = null;

      if (!dragStart) {
        return;
      }

      if (!dragStart.hasDragged) {
        dispatch({
          type: 'SLIDE_CHANGE',
          index: getState().lastIndex,
          animate: false,
        });

        return;
      }

      preventClickRef.current = true;

      const { activeIndex, lastIndex, slideLength } = getState();
      const timeElapsed = Math.max(new Date().getTime() - dragStart.startedAt, 1);
      const speed = Math.abs(((activeIndex - lastIndex) / timeElapsed) * 10000);

      let nextIndex = Math.round(activeIndex);

      if (speed > 20 && activeIndex !== lastIndex) {
        nextIndex = activeIndex < lastIndex ? lastIndex - 1 : lastIndex + 1;
      }

      dispatch({
        type: 'SLIDE_CHANGE',
        index: Math.min(Math.max(nextIndex, -1), slideLength + 1),
        animate: true,
      });
    },
    [dispatch, getState],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;

      const isImageButton = Boolean(target.closest('[data-gallery-image-button="true"]'));
      const isInteractiveElement = Boolean(target.closest('button, a, [role="button"]'));
      const shouldIgnoreDrag = Boolean(target.closest('[data-gallery-drag-ignore="true"]'));

      if (
        disabled ||
        event.pointerType !== 'mouse' ||
        event.button !== 0 ||
        getState().slideLength <= 1 ||
        shouldIgnoreDrag ||
        (isInteractiveElement && !isImageButton)
      ) {
        return;
      }

      preventClickRef.current = false;

      dragStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        lastIndex: getState().lastIndex,
        startedAt: new Date().getTime(),
        hasDragged: false,
      };

      event.currentTarget.setPointerCapture(event.pointerId);

      dispatch({
        type: 'DRAG_START',
        position: 0,
      });
    },
    [disabled, dispatch, getState],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const dragStart = dragStartRef.current;

      if (!dragStart) {
        return;
      }

      const deltaX = Math.abs(event.clientX - dragStart.x);
      const deltaY = Math.abs(event.clientY - dragStart.y);

      if (deltaX <= DRAG_TOLERANCE || deltaX <= deltaY) {
        return;
      }

      event.preventDefault();
      dragStart.hasDragged = true;

      const containerWidth = event.currentTarget.clientWidth || window.innerWidth;
      const offset =
        direction === 'rtl' ? event.clientX - dragStart.x : dragStart.x - event.clientX;

      const percentageOffset = offset / containerWidth;
      const { slideLength } = getState();

      dispatch({
        type: 'DRAG_MOVE',
        index: Math.min(Math.max(dragStart.lastIndex + percentageOffset, -1), slideLength + 1),
      });
    },
    [direction, dispatch, getState],
  );

  const handlePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      endDrag(event.currentTarget, event.pointerId);
    },
    [endDrag],
  );

  const handlePointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      endDrag(event.currentTarget, event.pointerId);
    },
    [endDrag],
  );

  const handleClickCapture = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (!preventClickRef.current) {
      return;
    }

    preventClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onClickCapture: handleClickCapture,
  };
};
